import { stringifyEntityRef } from "@backstage/catalog-model";
import type { Entity } from "@backstage/catalog-model";
import {
  Content,
  ErrorPanel,
  Header,
  Link,
  Page,
  Progress,
  Table,
} from "@backstage/core-components";
import type { TableColumn } from "@backstage/core-components";
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from "@backstage/core-plugin-api";
import { catalogApiRef, EntityRefLink } from "@backstage/plugin-catalog-react";
import { MenuItem, TextField } from "@material-ui/core";
import { useEffect, useState } from "react";

import {
  buildTasksUrl,
  fetchCatalogGroups,
  statusLabel,
  taskDisplayLabel,
  toUserEntityRef,
  type Task,
} from "./utils";

const columns: TableColumn<Task>[] = [
  {
    title: "Tarea",
    render: (t) => (
      <Link to={`/create/tasks/${t.id}`}>{taskDisplayLabel(t)}</Link>
    ),
  },
  {
    title: "Creada por",
    render: (t) => {
      const userRef = toUserEntityRef(t.createdBy);
      if (!userRef) {
        return t.createdBy ?? "—";
      }
      return <EntityRefLink entityRef={userRef} defaultKind="User" hideIcon />;
    },
  },
  {
    title: "Creada",
    render: (t) => new Date(t.createdAt).toLocaleString("es-ES"),
  },
  { title: "Estado", render: (t) => statusLabel(t.status) },
];

export const TaskListPage = () => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const identity = useApi(identityApiRef);
  const catalog = useApi(catalogApiRef);

  const [scope, setScope] = useState<"owned" | "all">("owned");
  const [team, setTeam] = useState<Entity>();
  const [teams, setTeams] = useState<Entity[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Task[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamsError, setTeamsError] = useState<Error>();

  // Optional dropdown data: a failure here must not hide the task list.
  useEffect(() => {
    let cancelled = false;
    fetchCatalogGroups(
      catalog,
      ["kind", "metadata.name", "metadata.namespace", "metadata.title"],
      200,
    )
      .then((items) => !cancelled && setTeams(items))
      .catch((e) => !cancelled && setTeamsError(e))
      .finally(() => !cancelled && setTeamsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [catalog]);

  useEffect(() => {
    let cancelled = false;
    setRows([]);
    setTotal(0);
    setError(undefined);
    setLoading(true);
    (async () => {
      let createdBy: string | undefined;
      let createdByGroup: string | undefined;
      if (scope === "owned") {
        createdBy = (await identity.getBackstageIdentity()).userEntityRef;
      } else if (team) {
        createdByGroup = stringifyEntityRef(team);
      }
      const base = await discovery.getBaseUrl("scaffolder");
      const res = await fetch(
        buildTasksUrl(base, {
          createdBy,
          createdByGroup,
          limit: pageSize,
          offset: page * pageSize,
        }),
      );
      if (!res.ok) {
        throw new Error(
          `Error ${res.status} al listar tareas: ${res.statusText}`,
        );
      }
      const body = await res.json();
      if (!cancelled) {
        setRows(body.tasks);
        setTotal(body.totalTasks);
      }
    })()
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [scope, team, page, pageSize, catalog, discovery, fetch, identity]);

  return (
    <Page themeId="home">
      <Header title="Lista de tareas" subtitle="Ejecuciones del Scaffolder" />
      <Content>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <TextField
            select
            id="tasklist-scope"
            label="Mostrar"
            name="scope"
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as "owned" | "all");
              setPage(0);
            }}
            style={{ flex: "1 1 200px", maxWidth: "100%" }}
          >
            <MenuItem value="owned">Mis tareas</MenuItem>
            <MenuItem value="all">Todas las tareas</MenuItem>
          </TextField>
          <TextField
            select
            id="tasklist-team"
            label="Equipo"
            name="team"
            value={team ? stringifyEntityRef(team) : ""}
            disabled={scope === "owned" || teamsLoading || !!teamsError}
            error={!!teamsError}
            onChange={(e) => {
              const selectedTeam = teams.find(
                (g) => stringifyEntityRef(g) === e.target.value,
              );
              if (e.target.value && !selectedTeam) {
                setError(
                  new Error(`No se encontró el equipo ${e.target.value}`),
                );
                return;
              }
              setTeam(selectedTeam);
              setPage(0);
            }}
            helperText={
              <>
                Filtra las tareas en “Todas las tareas”; no modifica tus
                permisos.
                {teamsLoading && " Cargando equipos…"}
                {teamsError && " Filtro por equipo no disponible."}
              </>
            }
            style={{ flex: "1 1 280px", maxWidth: "100%" }}
          >
            <MenuItem value="">Todos los equipos</MenuItem>
            {teams.map((g) => (
              <MenuItem
                key={stringifyEntityRef(g)}
                value={stringifyEntityRef(g)}
              >
                {g.metadata.title ?? g.metadata.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
        {teamsError && (
          <div role="status" style={{ marginBottom: 16 }}>
            No se pudieron cargar los equipos; se muestran las tareas sin filtro
            por equipo.
          </div>
        )}
        {error && <ErrorPanel error={error} />}
        {loading && <Progress />}
        {!error && (
          <Table<Task>
            title={`Tareas (${total})`}
            columns={columns}
            data={rows}
            isLoading={loading}
            page={page}
            totalCount={total}
            onPageChange={setPage}
            onRowsPerPageChange={(n) => {
              setPageSize(n);
              setPage(0);
            }}
            options={{ pageSize, search: false, paging: true, sorting: false }}
            localization={{ body: { emptyDataSourceMessage: "No hay tareas" } }}
          />
        )}
      </Content>
    </Page>
  );
};
